import { produce } from 'immer';

import { CreditConfiguration } from '@api/consumerloans-public-api';

import { StoreHelper } from '../storeHelper';

export interface SimulationInputData {
	amount?: number;
	term?: number;
	installment?: number;
	isChanged?: boolean;
}

export interface TempState {
	creditTypeSelected: CreditConfiguration;
	inputData: SimulationInputData;
	isValidSimulation: boolean;
	showStickyBar: boolean;
	newSimulation: boolean;
	showedAdjustedPaymentTermSimulation: boolean;
	isAdjustedSimulation: boolean;
	isMonthlyInstallmentInputAtTheTop: boolean;
}

export const tempInitialState: TempState = {
	creditTypeSelected: {},
	inputData: {
		amount: undefined,
		term: undefined,
		installment: undefined,
	},
	isValidSimulation: true,
	showStickyBar: true,
	newSimulation: true,
	showedAdjustedPaymentTermSimulation: false,
	isAdjustedSimulation: false,
	isMonthlyInstallmentInputAtTheTop: false,
};

interface UseTempStoreOutput extends TempState {
	saveCreditTypeSelected: (data: CreditConfiguration) => void;
	saveSimulationInputData: (data: SimulationInputData) => void;
	changeIsValidSimulation: (data: boolean) => void;
	changeShowStickyBar: (data: boolean) => void;
	saveNewSimulation: (data: boolean) => void;
	setShowedAdjustedPaymentTermSimulation: () => void;
	setIsAdjustedSimulation: (data: boolean) => void;
	setIsMonthlyInstallmentInputAtTheTop: (data: boolean) => void;
}

export const useTempStore = StoreHelper.createStore<UseTempStoreOutput>(
	(set, get) => ({
		...tempInitialState,
		saveCreditTypeSelected: (creditTypeSelected: CreditConfiguration) =>
			set(
				produce((state: TempState): TempState => {
					const innerState = get();

					return {
						...innerState,
						creditTypeSelected,
					};
				}),
				false,
				'@@CONSUMERLOANSPUBLIC/TEMP/saveCreditTypeSelected'
			),
		saveSimulationInputData: (simulationInputData: SimulationInputData) =>
			set(
				produce((state: TempState): TempState => {
					const innerState = get();
					const amount = simulationInputData.amount ? simulationInputData.amount : state.inputData.amount;
					const term = simulationInputData.term ? simulationInputData.term : state.inputData.term;
					const hasChanged = simulationInputData.amount || simulationInputData.term ? true : false;
					const installment = simulationInputData.installment
						? simulationInputData.installment
						: state.inputData.installment;

					return {
						...innerState,
						inputData: {
							amount,
							term,
							installment,
							// marked when some input value was changed
							isChanged: hasChanged,
						},
					};
				}),
				false,
				'@@CONSUMERLOANSPUBLIC/TEMP/saveSimulationInputData'
			),
		changeIsValidSimulation: (isValidSimulation: boolean) =>
			set(
				produce((state: TempState): TempState => {
					const innerState = get();

					return {
						...innerState,
						isValidSimulation,
					};
				}),
				false,
				'@@CONSUMERLOANSPUBLIC/TEMP/changeIsValidSimulation'
			),
		changeShowStickyBar: (showStickyBar: boolean) =>
			set(
				produce((state: TempState): TempState => {
					const innerState = get();

					return {
						...innerState,
						showStickyBar,
					};
				}),
				false,
				'@@CONSUMERLOANSPUBLIC/TEMP/changeShowStickyBar'
			),
		saveNewSimulation: (isNewSimulation: boolean) =>
			set(
				produce((state: TempState): TempState => {
					const innerState = get();

					return {
						...innerState,
						newSimulation: isNewSimulation,
					};
				}),
				false,
				'@@CONSUMERLOANSPUBLIC/TEMP/saveNewSimulation'
			),
		setShowedAdjustedPaymentTermSimulation: () =>
			set(
				produce((state: TempState): TempState => {
					const innerState = get();

					return {
						...innerState,
						showedAdjustedPaymentTermSimulation: true,
					};
				}),
				false,
				'@@CONSUMERLOANSPUBLIC/TEMP/setShowedAdjustedPaymentTermSimulation'
			),
		setIsAdjustedSimulation: (isAdjustedSimulation: boolean) =>
			set(
				produce((state: TempState): TempState => {
					const innerState = get();

					return {
						...innerState,
						isAdjustedSimulation: isAdjustedSimulation,
					};
				}),
				false,
				'@@CONSUMERLOANSPUBLIC/TEMP/setIsAdjustedSimulation'
			),
		setIsMonthlyInstallmentInputAtTheTop: (isMonthlyInstallmentInputAtTheTop: boolean) =>
			set(
				produce((state: TempState): TempState => {
					const innerState = get();
					return {
						...innerState,
						isMonthlyInstallmentInputAtTheTop,
					};
				}),
				false,
				'@@CONSUMERLOANSPUBLIC/TEMP/isMonthlyInstallmentInputAtTheTop'
			),
	}),
	'@@CONSUMERLOANSPUBLIC/TEMP/'
);
